import React, { useCallback, useMemo, useState } from 'react';
import { todayISO } from '../domain/clock';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import EmptyState from '../components/EmptyState';
import Tabs from '../components/Tabs';
import StudentSidebar from '../components/students/StudentSidebar';
import StudentDetailsTab from '../components/students/tabs/StudentDetailsTab';
import FamilyContactsTab from '../components/students/tabs/FamilyContactsTab';
import FamilyPortalTab, { withFamilyPortalTab } from '../components/students/tabs/FamilyPortalTab';
import PracticeLogTab from '../components/students/tabs/PracticeLogTab';
import AttendanceTab from '../components/students/tabs/AttendanceTab';
import RepertoireTab from '../components/students/tabs/RepertoireTab';
import MessageHistoryTab from '../components/students/tabs/MessageHistoryTab';
import NewMessageDrawer from '../components/students/NewMessageDrawer';
import PracticeTimeDrawer from '../components/students/PracticeTimeDrawer';
import RepertoirePieceDrawer from '../components/students/RepertoirePieceDrawer';
import SellPackageWizard from '../components/packages/SellPackageWizard';
import PackageActivityDrawer from '../components/students/PackageActivityDrawer';
import EditStudentDrawer from '../components/students/edit/EditStudentDrawer';
import EditStudentDetailsDrawer from '../components/students/edit/EditStudentDetailsDrawer';
import EditNoteDrawer from '../components/students/edit/EditNoteDrawer';
import EditLessonSettingsDrawer from '../components/students/edit/EditLessonSettingsDrawer';
import EditFamilyDrawer from '../components/students/edit/EditFamilyDrawer';
import FamilyContactDrawer from '../components/students/edit/FamilyContactDrawer';
import SetMakeupCreditsDrawer from '../components/students/edit/SetMakeupCreditsDrawer';
import useStudentEdit, { SURFACES } from '../hooks/useStudentEdit';
import useStudentAttendance from '../hooks/useStudentAttendance';
import useMessageHistory from '../hooks/useMessageHistory';
import useNewMessage from '../hooks/useNewMessage';
import useStudentPackageForecast, { convertibleLessonsOf } from '../hooks/useStudentPackageForecast';
import { useStudents } from '../data/useStudents';
import { reading } from '../domain/reading';
import ReadFailed from '../components/ReadFailed';
import { useFamilies } from '../data/useFamilies';
import { useMakeup, MAKEUP_CREDITS_MAX, MAKEUP_REASON_MAX } from '../data/useMakeup';
import { useEvents } from '../data/useEvents';
import { useAttendance } from '../data/useAttendance';
import { usePractice } from '../data/usePractice';
import { useRepertoire } from '../data/useRepertoire';
import { useNotifications } from '../data/useNotifications';
import useMessageLog from '../data/useMessageLog';
import { discardAttachment, uploadAttachment } from '../data/messageAttachments';
import {
  composeChoices, composeFromChoices, composeGroups, defaultHistoryPeriod, historyPeriods, reachedStudents,
} from '../domain/notifications';
import { usePackages } from '../data/usePackages';
import { useTags } from '../data/useTags';
import { useCategories } from '../data/useCategories';
import useNames from '../hooks/useNames';
import { normaliseTimeZone } from '../domain/timeZone';
import { displayTimeZone } from '../lib/intlOptions';
import { useSettings } from '../data/useSettings';
import { useCurrency } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import s from './StudentDetailPage.module.css';

// Student profile: a left rail of standing facts about the student, and tabs of
// the things that accumulate around them.
//
// Assembly only — each tab is its own component, the attendance fold is a hook,
// and the four drawers own their own markup.

// MMS's seven: its Student Portal tab is our Family Portal tab (#770), last as
// MMS has it, and drawn only while the Family Portal is open (#769). Appended,
// so every other tab keeps its index.
const BASE_TABS = ['Student Details', 'Family Contacts', 'Practice Log',
                   'Attendance & Notes', 'Repertoire', 'Message History'];
const PORTAL_TAB = BASE_TABS.length;



const BLANK_TIME = () => ({ practicedOn: todayISO(), minutes: 30, note: '' });
const BLANK_PIECE = { title: '', composer: '', status: 'assigned', note: '' };


export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    getStudent, loading, failed: studentsFailed, updateStudent, students: allStudents,
  } = useStudents();
  const {
    byId: familyById, loading: familiesLoading, contactsLoading, contactsFailed, updateFamily,
    contactsFor, billingContactFor, saveContact, removeContact,
    families: allFamilies, contacts: allContacts,
  } = useFamilies();
  // The studio's categories, for the Lesson Settings drawer's picker. Falls
  // back to the built-in six for a studio that has never saved any, as the
  // wizard's select and the event form's do.
  const { categories } = useCategories();
  const { settings } = useSettings();
  const {
    summaryFor: makeupSummaryFor, policy: makeupPolicy,
    loaded: makeupLoaded, setCredits,
  } = useMakeup();
  const {
    occurrencesBetween, priceFor, loading: eventsLoading, failed: eventsFailed,
  } = useEvents();
  const { statusFor, loading: attendanceLoading, failed: attendanceFailed } = useAttendance();
  const {
    forStudent: practiceFor, summaryFor: practiceSummaryFor, addSession, removeSession,
    loading: practiceLoading, failed: practiceFailed,
  } = usePractice();
  const {
    forStudent: repertoireFor, addPiece, setStatus: setPieceStatus, removePiece,
    loading: repertoireLoading, failed: repertoireFailed,
  } = useRepertoire();
  const {
    summaryFor: packageSummaryFor, activityFor, forecastFor: packageForecastFor,
    isEntryActive: isPackageEntryActive, sellPackage, loading: packagesLoading,
    failed: packagesFailed,
  } = usePackages();
  // The studio's managed tags, so the Details tab can say which of them this
  // student is in — membership lives on the tag (#138). `saveTag` and `tagsFor`
  // are the Details drawer's half (#328): its pickers write memberships.
  const { tags, tagsFor, saveTag, loaded: tagsLoaded } = useTags();
  const currency = useCurrency();
  const names = useNames();

  const [tab, setTab] = useState(0);
  const TABS = withFamilyPortalTab(BASE_TABS);
  const [openDrawer, setOpenDrawer] = useState(null); // null | 'time' | 'piece' | 'package' | 'activity' | 'credits'
  const [timeForm, setTimeForm] = useState(BLANK_TIME);
  const [pieceForm, setPieceForm] = useState(BLANK_PIECE);
  // The family's contacts (#176). `null` is closed; an object is the contact
  // being edited, and `{}` — falsy `id` — is Add. One piece of state, so
  // "which contact" and "is it open" cannot disagree.
  const [contactDraft, setContactDraft] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);

  const student = getStudent(id);
  const family = student?.familyId ? familyById(student.familyId) : null;
  const { history, stats } = useStudentAttendance({ student, occurrencesBetween, statusFor });
  const {
    messages, sendAgain, withdraw: withdrawMessage, sendTeacherMessage, loading: outboxLoading,
    failed: outboxFailed,
  } = useNotifications();

  // Message History (#522): MMS's period, Last 3 Months to begin with, and
  // the delivery log for every period but Scheduled Queue — read only while
  // the tab is open, by this student and family (src/data/useMessageLog.js).
  const messagePeriods = useMemo(() => historyPeriods(), []);
  const [messagePeriod, setMessagePeriod] = useState(defaultHistoryPeriod);
  const readsLog = Boolean(messagePeriods.find((p) => p.code === messagePeriod)?.readsLog);
  const { rows: messageLog, loading: logLoading } = useMessageLog({
    studentId: student?.id || null, familyId: student?.familyId || null, enabled: tab === 5 && readsLog,
  });
  const messageRows = useMessageHistory({
    messages, logged: readsLog ? messageLog : [], student, period: messagePeriod,
  });
  // New Message ▾ (#522): each family contact with an email, everybody — and
  // the composer behind it. Not the student: a student is not a message
  // destination (#792).
  const familyContacts = family ? contactsFor(family.id) : null;
  const messageChoices = useMemo(
    () => (student ? composeChoices(family, familyContacts || []) : []),
    [student, family, familyContacts],
  );
  // CC me (#547) copies the teacher at the address they signed in with, and
  // From (#600) offers the studio's address or that one.
  const { user } = useAuth();
  // The teacher's choice is labelled with the name it is sent under: the
  // Teacher Name Format's (#797, deliver.js `namedTeacher`).
  const teacherName = names.teacher(user?.displayName);
  const fromChoices = useMemo(() => composeFromChoices({
    studioName: settings?.name,
    studioEmail: settings?.messaging?.studioEmail,
    teacherName,
    teacherEmail: user?.email,
  }), [settings?.name, settings?.messaging?.studioEmail, teacherName, user?.email]);
  // Send Email's date and time (#599) are read on the clock the tab writes
  // every instant in: the family's zone, else the studio's.
  const composeZone = normaliseTimeZone((family?.timeZone || null) || displayTimeZone(settings));
  // Whose tabs a message lists on (#607): every student it reached, read off
  // the studio's lists at Send.
  const reachedFor = useCallback((to) => reachedStudents({
    to, students: allStudents, families: allFamilies, contacts: allContacts,
  }), [allStudents, allFamilies, allContacts]);
  const composer = useNewMessage({
    studentId: student?.id || null,
    familyId: student?.familyId || null,
    send: sendTeacherMessage,
    myEmail: user?.email || null,
    fromChoices,
    timeZone: composeZone,
    reachedFor,
    // Attach Files (#605): each pick uploaded to Storage at once, and
    // deleted again when it will not be sent.
    upload: uploadAttachment,
    discard: discardAttachment,
  });
  // To's studio-wide `Select...` groups (#600), widened by "Show all". The
  // teacher's group is named First Last: no MMS reading puts the Teacher
  // Name Format on it (#797).
  const groupTeacher = names.teacherFullName(user?.displayName);
  const messageGroups = useMemo(() => (composer.isOpen ? composeGroups({
    students: allStudents,
    families: allFamilies,
    contacts: allContacts,
    teacherName: groupTeacher,
    showAll: composer.showAll,
  }) : []), [composer.isOpen, composer.showAll, allStudents, allFamilies, allContacts, groupTeacher]);

  // The package running out (#181). Folded from the same `history` the
  // Attendance tab reads rather than a second expansion of the calendar (the
  // hook's header says why).
  const packageForecast = useStudentPackageForecast({
    student, history, priceFor, forecastFor: packageForecastFor,
  });

  // The five edit drawers (#120), held here because only one may be open at a
  // time.
  //
  // Before the early returns below, like every other hook on this page: a hook
  // after a conditional return is a different hook order on the next render.
  const edit = useStudentEdit({
    student, family, updateStudent, updateFamily, currency,
    tags, tagsFor, saveTag, tagsLoaded,
  });

  // The roster's read stopped before it answered (#647): say so, with Try
  // again, rather than "Loading…" for good.
  if (loading && !student && studentsFailed) {
    return <AppShell title="Student"><ReadFailed /></AppShell>;
  }
  if (loading && !student) {
    return <AppShell title="Student"><div className={s.loading}>Loading…</div></AppShell>;
  }
  if (!student) {
    return (
      <AppShell title="Student">
        <EmptyState
          // Read, and this student is not in it: the roster has answered,
          // which the `loading` return above has already waited for.
          reading={reading(!loading, 0)}
          title="Student not found"
          description="This student may have been removed."
          actionLabel="Back to Students"
          onAction={() => navigate('/students')}
        />
      </AppShell>
    );
  }

  const name = names.student(student);
  // Which clock the times on this profile are in: the family's own when it has
  // one, otherwise the studio's (domain/timeZone.js). `displayTimeZone` falls
  // back again to this machine's zone, so this is null only when the family,
  // the studio and the browser all decline — when `Intl.DateTimeFormat()`
  // throws — and a line naming no zone would be worse than no line.
  const profileTimeZone = normaliseTimeZone(
    (family?.timeZone || null) || displayTimeZone(settings),
  );
  const closeDrawer = () => setOpenDrawer(null);

  const saveTime = async () => {
    await addSession({ studentId: student.id, ...timeForm });
    setTimeForm(BLANK_TIME());
    closeDrawer();
  };

  const savePiece = async () => {
    await addPiece({ studentId: student.id, ...pieceForm });
    setPieceForm(BLANK_PIECE);
    closeDrawer();
  };

  // What `Apply Package Time → Existing events…` will convert:
  // `convertibleLessonsOf`, shared with the studio-level `+ Add Package` on
  // `PackageBalancesPanel` (#377), where its four conditions are stated.
  const convertible = convertibleLessonsOf({ history, student, priceFor });

  const sell = async (sale) => {
    await sellPackage(sale);
    closeDrawer();
  };

  return (
    <AppShell title="Students">
      <div className={s.page}>
        <button className={s.back} onClick={() => navigate('/students')}>‹ Back to Students</button>
        <div className={s.name}>{name}</div>

        <div className={s.columns}>
          <StudentSidebar
            student={student}
            name={name}
            credits={makeupSummaryFor(student.id, student.durationMin)}
            // Which of the figures' ledgers have answered (#629).
            reads={{ makeup: makeupLoaded, packages: !packagesLoading }}
            packageSummary={packageSummaryFor(student.id)}
            packageForecast={packageForecast}
            onViewFamily={() => navigate('/families')}
            onAddPackage={() => setOpenDrawer('package')}
            onPackageActivity={() => setOpenDrawer('activity')}
            onSetCredits={() => setOpenDrawer('credits')}
            onEditProfile={() => edit.open(SURFACES.profile)}
            onEditLesson={() => edit.open(SURFACES.lesson)}
            onEditNote={() => edit.open(SURFACES.note)}
          />

          <div>
            <Tabs tabs={TABS} current={tab} onChange={setTab} />

            {/* The family's zone, or the studio's — one zone per account, and a
                family that has never been asked reads in the studio's (see
                domain/timeZone.js). Resolved here rather than in the tab
                because the zone lives on the family document and the tab is
                handed a student. */}
            {tab === 0 ? (
              <StudentDetailsTab
                student={student}
                timeZone={profileTimeZone}
                tags={tags}
                onEdit={() => edit.open(SURFACES.details)}
              />
            ) : null}

            {tab === 1 ? (
              <FamilyContactsTab
                student={student}
                name={name}
                family={family}
                familiesLoading={familiesLoading}
                contactsLoading={contactsLoading}
                contactsFailed={contactsFailed}
                onViewFamily={() => navigate('/families')}
                // The family account's own name (#319). Only where there is a
                // family to edit: a student with no family account, or one
                // pointing at a family document that is gone, renders the
                // explanation the tab already carries and no pencil.
                onEditFamily={family ? () => edit.open(SURFACES.family) : null}
                // The contacts, and the three things a card can do with one.
                // Same guard as the pencil above: with no family there is
                // nothing to add a contact to.
                contacts={family ? contactsFor(family.id) : []}
                billingContact={family ? billingContactFor(family.id) : null}
                onAddContact={family ? () => setContactDraft({}) : null}
                onEditContact={family ? (c) => setContactDraft(c) : null}
                onRemoveContact={family ? removeContact : null}
              />
            ) : null}

            {tab === 2 ? (
              <PracticeLogTab
                sessions={practiceFor(student.id)}
                read={!practiceLoading}
                failed={practiceFailed}
                summary={practiceSummaryFor(student.id, todayISO())}
                today={todayISO()}
                onAddTime={() => setOpenDrawer('time')}
                onRemoveSession={removeSession}
              />
            ) : null}

            {tab === 3 ? (
              <AttendanceTab
                history={history}
                stats={stats}
                read={!eventsLoading && !attendanceLoading}
                failed={(eventsLoading && eventsFailed) || (attendanceLoading && attendanceFailed)}
              />
            ) : null}

            {tab === 5 ? (
              <MessageHistoryTab
                rows={messageRows}
                read={!outboxLoading && !(readsLog && logLoading)}
                // The log is a one-off read, not a listener: an error is its
                // answer, and the tab lists the outbox without it (#647).
                failed={outboxFailed}
                timeZone={profileTimeZone}
                onSendAgain={sendAgain}
                period={messagePeriod}
                periods={messagePeriods}
                onPeriodChange={setMessagePeriod}
                choices={messageChoices}
                onCompose={composer.open}
                // Forward carries the subject and the words, and no files
                // (#605, src/hooks/useNewMessage.js says why).
                onForward={composer.forward}
                onWithdraw={withdrawMessage}
              />
            ) : null}

            {TABS.length > PORTAL_TAB && tab === PORTAL_TAB ? (
              <FamilyPortalTab
                uid={user?.uid || null}
                student={student}
                family={family}
                contacts={family ? contactsFor(family.id) : []}
                contactsLoading={contactsLoading}
                // The preview's From and sign-off: the studio's name and
                // address as Email & SMS holds them, and the teacher's own
                // first name, as MMS signs its template.
                studioName={settings?.name || null}
                studioEmail={settings?.messaging?.studioEmail || null}
                teacherFirstName={names.teacherParts(user?.displayName).firstName}
              />
            ) : null}

            {tab === 4 ? (
              <RepertoireTab
                pieces={repertoireFor(student.id)}
                read={!repertoireLoading}
                failed={repertoireFailed}
                onAddPiece={() => setOpenDrawer('piece')}
                onStatusChange={setPieceStatus}
                onRemovePiece={removePiece}
              />
            ) : null}
          </div>
        </div>
      </div>

      <NewMessageDrawer composer={composer} choices={messageChoices} groups={messageGroups} />

      <PracticeTimeDrawer
        open={openDrawer === 'time'}
        form={timeForm}
        onChange={(patch) => setTimeForm((f) => ({ ...f, ...patch }))}
        onClose={closeDrawer}
        onSave={saveTime}
      />

      <RepertoirePieceDrawer
        open={openDrawer === 'piece'}
        form={pieceForm}
        onChange={(patch) => setPieceForm((f) => ({ ...f, ...patch }))}
        onClose={closeDrawer}
        onSave={savePiece}
      />

      {/* `Add Package` — the three-step wizard (#181). A teacher picks a
          template and a start date and never types a price; the component's
          header says what is on screen and why.

          `teacherName` is the studio's name, a stated substitution: MMS writes
          the ledger row as `Lesson - <template> - <teacher>` and models
          teachers as entities, which this app does not (one studio, one
          owner), and the Business Settings form defaults the studio name to
          the owner's own name.

          `settings.name` is the normalised studio record's own field (guarded
          by test/domain/packageBounds.test.js), and the raw field rather than
          the `name` reader beside it, which falls back to `UNNAMED_STUDIO`
          ("Your Studio"): a placeholder is right on a screen and wrong on a
          ledger row a family reads. With no studio name set,
          `packageChargeDescription` drops the segment. */}
      <SellPackageWizard
        open={openDrawer === 'package'}
        studentName={name}
        studentId={student.id}
        familyId={student.familyId}
        teacherName={settings.name || ''}
        convertible={convertible}
        todayISO={todayISO()}
        onClose={closeDrawer}
        onSell={sell}
      />

      {/*
        The core's predicate rather than a hand-rolled !voidedAt: a tombstone is
        two fields and only the rule knows both (core/src/packages.rs, #70).
        `usePackages` shapes the document for the crossing.
      */}
      <PackageActivityDrawer
        open={openDrawer === 'activity'}
        activity={activityFor(student.id).filter(isPackageEntryActive)}
        read={!packagesLoading}
        failed={packagesFailed}
        forecast={packageForecast}
        onClose={closeDrawer}
      />

      {/* `Set Make-Up Credits` (#178). Rendered only while open, so its number
          box is seeded from the balance as it is now; it owns its own state,
          like `FamilyContactDrawer` below.

          Here rather than inside `StudentSidebar`, where its button is,
          because importing it reaches `src/lib/firebase.js`, which the `ui`
          jest project has no mapper for (the rail's header). */}
      {openDrawer === 'credits' ? (
        <SetMakeupCreditsDrawer
          name={name}
          summary={makeupSummaryFor(student.id, student.durationMin)}
          policy={makeupPolicy}
          loaded={makeupLoaded}
          creditsMax={MAKEUP_CREDITS_MAX}
          reasonMax={MAKEUP_REASON_MAX}
          durationMin={student.durationMin}
          onSave={({ total, reason }) => setCredits({ student, total, reason })}
          onClose={closeDrawer}
        />
      ) : null}

      {/* The five edit drawers. Each takes the same five props from the hook,
          because each is the same thing: a slice of a document, a keyed bag of
          refusals, and a Save that stays clickable. */}
      <EditStudentDrawer
        open={edit.surface === SURFACES.profile}
        form={edit.form}
        errors={edit.errors}
        saving={edit.saving}
        onChange={edit.set}
        onClose={edit.close}
        onSave={edit.save}
      />

      <EditStudentDetailsDrawer
        open={edit.surface === SURFACES.details}
        form={edit.form}
        errors={edit.errors}
        saving={edit.saving}
        // Whether to offer a zone for this family at all — MMS's "Enable
        // Multi-Time Zone Selection", off unless the studio teaches across
        // zones. The wizard's family step gates the same field the same way.
        multiTimeZone={settings.scheduling.multiTimeZone}
        hasFamily={Boolean(student.familyId)}
        tags={tags}
        tagsLoaded={tagsLoaded}
        onToggleTag={edit.toggleTag}
        onCreateTag={edit.createTag}
        onChange={edit.set}
        onClose={edit.close}
        onSave={edit.save}
      />

      <EditNoteDrawer
        open={edit.surface === SURFACES.note}
        form={edit.form}
        errors={edit.errors}
        saving={edit.saving}
        onChange={edit.set}
        onClose={edit.close}
        onSave={edit.save}
      />

      <EditLessonSettingsDrawer
        open={edit.surface === SURFACES.lesson}
        form={edit.form}
        errors={edit.errors}
        saving={edit.saving}
        categories={categories}
        onChange={edit.set}
        onChooseCategory={(catId) => edit.chooseCategory(categories, catId)}
        onClose={edit.close}
        onSave={edit.save}
      />

      <EditFamilyDrawer
        open={edit.surface === SURFACES.family}
        form={edit.form}
        errors={edit.errors}
        saving={edit.saving}
        onChange={edit.set}
        onClose={edit.close}
        onSave={edit.save}
      />

      {/* Add and edit are the same drawer — `contactDraft` carries the contact
          or `{}` for a new one. It owns its form state rather than going through
          `useStudentEdit`, which covers the five slices of one
          student-and-family pair; a contact is a document of its own. */}
      <FamilyContactDrawer
        open={Boolean(contactDraft)}
        contact={contactDraft && contactDraft.id ? contactDraft : null}
        saving={contactSaving}
        onClose={() => setContactDraft(null)}
        onSave={async (form) => {
          if (!family) return;
          setContactSaving(true);
          try {
            // `base` is the row this drawer was seeded from, which
            // `saveContact` diffs the patch against (#396), not the provider's
            // live window: a change from another device while the drawer is open
            // would be in that, and the form's stale copy would revert it.
            // `contactDraft` is `{}` on a create, with no base to want.
            await saveContact({
              ...form,
              id: contactDraft?.id || null,
              familyId: family.id,
              base: contactDraft?.id ? contactDraft : null,
            });
            setContactDraft(null);
          } finally { setContactSaving(false); }
        }}
      />
    </AppShell>
  );
}
